import { useTranslations } from '@aikoa/i18n/react'
import * as Clipboard from 'expo-clipboard'
import { cacheDirectory, EncodingType, writeAsStringAsync } from 'expo-file-system/legacy'
import { shareAsync } from 'expo-sharing'
import { useCallback, useState } from 'react'
import { Platform, View } from 'react-native'
import { Body } from '~/components/typography/Content'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { useCreateInboundAddress, useUserInboundAddress } from '~/data/user/inboundAddress'
import { useUserProfile } from '~/data/user/userProfile'
import { cn } from '~/lib/utils'

type CreateVCardProps = {
  email: string
  contactName: string
  note: string
}

function createVCard({ email, contactName, note }: CreateVCardProps): string {
  const escapedNote = note.replace(/\n/g, String.raw`\n`).replace(/,/g, String.raw`\,`)

  /* eslint-disable i18next/no-literal-string */
  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${contactName}`,
    `N:;${contactName};;;`,
    'ORG:Aikoa',
    `EMAIL;TYPE=WORK:${email}`,
    `NOTE:${escapedNote}`,
    'END:VCARD',
  ].join('\r\n')
  /* eslint-enable i18next/no-literal-string */
}

async function downloadContactCard(vCardProps: CreateVCardProps): Promise<void> {
  const vCardContent = createVCard(vCardProps)

  if (Platform.OS === 'web') {
    const blob = new Blob([vCardContent], { type: 'text/vcard' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // eslint-disable-next-line i18next/no-literal-string
    link.download = 'aikoa.vcf'
    document.body.append(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } else {
    // eslint-disable-next-line i18next/no-literal-string
    const filePath = `${cacheDirectory}aikoa.vcf`

    await writeAsStringAsync(filePath, vCardContent, {
      encoding: EncodingType.UTF8,
    })

    await shareAsync(filePath, {
      mimeType: 'text/vcard',
      UTI: 'public.vcard',
    })
  }
}

export function InboundEmailView({
  className,
  showInstructions = true,
  onSaveToContactsPress,
}: {
  className?: string
  showInstructions?: boolean
  onSaveToContactsPress?: () => void
}) {
  const { data: inboundAddress, isLoading: isInboundAddressLoading } = useUserInboundAddress()
  const { data: profile } = useUserProfile()

  const createInboundAddressMutation = useCreateInboundAddress()
  const [copied, setCopied] = useState(false)

  const handleCopyEmail = useCallback(async () => {
    if (inboundAddress?.fullAddress) {
      await Clipboard.setStringAsync(inboundAddress.fullAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [inboundAddress])

  const { t: tCommon } = useTranslations('common')
  const { t } = useTranslations('user')

  const handleDownloadContactCard = useCallback(async () => {
    if (inboundAddress?.fullAddress) {
      onSaveToContactsPress?.()

      await downloadContactCard({
        email: inboundAddress.fullAddress,
        contactName: profile?.name?.trim()
          ? tCommon(($) => $.inboundEmail.vCardName, {
              realtorName: profile.name.trim(),
            })
          : tCommon(($) => $.inboundEmail.vCardNameFallback),
        note: tCommon(($) => $.inboundEmail.vCardNote),
      })
    }
  }, [inboundAddress, onSaveToContactsPress, profile?.name, tCommon])

  const handleCreateInboundAddress = useCallback(() => {
    createInboundAddressMutation.mutate()
  }, [createInboundAddressMutation])

  return (
    <View className={cn('flex-1 gap-3', className)}>
      {showInstructions && (
        <Body className="text-center">
          {t(($) => $.profile.realtor.inboundEmail.forwardLatestHint)}
        </Body>
      )}

      {isInboundAddressLoading ? (
        <Text className="text-sm text-muted-foreground">
          {t(($) => $.profile.realtor.inboundEmail.loading)}
        </Text>
      ) : inboundAddress ? (
        <View className="gap-6">
          <View className="flex-1 flex-row items-center justify-center rounded-lg bg-muted px-4 py-3">
            <Text className="text-center font-mono text-lg text-foreground">
              {inboundAddress.fullAddress}
            </Text>
          </View>
          <View className="flex-row justify-center gap-2">
            <Button variant="outline" size="lg" onPress={handleCopyEmail}>
              <ButtonLabel>
                {copied
                  ? tCommon(($) => $.inboundEmail.copied)
                  : tCommon(($) => $.inboundEmail.copyEmail)}
              </ButtonLabel>
            </Button>

            <Button variant="default" size="lg" onPress={handleDownloadContactCard}>
              <ButtonLabel>{t(($) => $.profile.realtor.inboundEmail.saveToContacts)}</ButtonLabel>
            </Button>
          </View>
        </View>
      ) : (
        <Button
          variant="outline"
          onPress={handleCreateInboundAddress}
          disabled={createInboundAddressMutation.isPending}>
          <Text>
            {createInboundAddressMutation.isPending
              ? t(($) => $.profile.realtor.inboundEmail.creating)
              : t(($) => $.profile.realtor.inboundEmail.createAddress)}
          </Text>
        </Button>
      )}
    </View>
  )
}
